import styles from './Media.module.css'
import ImgixClient from '@imgix/js-core'
import classNames from 'classnames'
import type { ImageConfig } from './types'

const FALLBACK_IMAGE_URL = 'https://huel.imgix.net/media/fallback-image.png'

const getClient = (url: URL) => {
  const domain = url.hostname.includes('huel-crm') ? 'huel-crm.imgix.net' : 'huel.imgix.net'

  return new ImgixClient({
    domain,
    includeLibraryParam: false,
  })
}

const getSrcSet = (src: string, config: ImageConfig) => {
  const url = new URL(src)
  const path = url.pathname.replace('cdn.huel.io/', '')
  const client = getClient(url)
  return client.buildSrcSet(
    path,
    {
      fit: 'crop',
      crop: 'entropy',
      auto: 'format,compress',
      ...(config.size.style === 'fixed' && { w: config.size.value }),
    },
    {
      disablePathEncoding: true, // INFO: This is needed because S3 replaces spaces in the URI with '+' signs, which imgix is then encoding into %2B which is breaking the image.
    },
  )
}

interface ImageProps {
  src: string
  config: ImageConfig
}

export const Image = ({ src = FALLBACK_IMAGE_URL, config: customConfig }: ImageProps) => {
  if (!src) {
    return null
  }

  const config: ImageConfig = {
    loading: 'lazy',
    fetchPriority: 'auto',
    ...customConfig,
  }

  const sizes = config.size.style === 'variable' ? config.size.value : '(max-width: 0px) 100vw, 100vw'

  const pictureClasses = classNames(styles.Media, {
    [styles[`Media--auto-width`]]: config.widthAuto,
  })

  const imgClasses = classNames(styles.Media__image, {
    [styles[`Media__image--${customConfig.objectFit}`]]: typeof customConfig.objectFit !== 'undefined',
    [styles[`Media__image--auto-width`]]: config.widthAuto,
  })

  const otherProps = {
    fetchPriority: config.fetchPriority, // HACK: This property doesn't exist in the HTMLImageElement type, but it's supported by the browser. So we need to spread it to avoid errors.
  }

  return (
    <picture className={pictureClasses}>
      <img
        className={imgClasses}
        alt={config.alt}
        srcSet={getSrcSet(src, config)}
        loading={config.loading}
        {...(config.size.style !== 'fixed' && { sizes })}
        {...otherProps}
      />
    </picture>
  )
}
